import * as d3 from 'd3';
import { isNil, round } from 'lodash';

const DATE_FORMAT_INITIAL = '%m.%Y';
const AXIS_REFERENCE_VALUES = [1.0, 1.5, 2.5, 5, 7.5];

export const locale = d3.timeFormatLocale({
    dateTime: '%A, %e %B %Y г. %X',
    date: '%d.%m.%Y',
    time: '%H:%M:%S',
    periods: ['AM', 'PM'],
    days: ['воскресенье', 'понедельник', 'вторник', 'среда', 'четверг', 'пятница', 'суббота'],
    shortDays: ['вс', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб'],
    months: [
        'январь',
        'февраль',
        'март',
        'апрель',
        'май',
        'июнь',
        'июль',
        'август',
        'сентябрь',
        'октябрь',
        'ноябрь',
        'декабрь',
    ],
    shortMonths: ['янв', 'фев', 'март', 'апр', 'май', 'июнь', 'июль', 'авг', 'сен', 'окт', 'ноя', 'дек'],
});

export const numberLocale = d3.formatLocale({
    decimal: '.',
    thousands: ' ',
    grouping: [3],
});

export const timeParse = d3.timeParse(DATE_FORMAT_INITIAL);
export const timeFormat = locale.format(DATE_FORMAT_INITIAL);

export const formatMillisecond = locale.format('.%L'),
    formatSecond = locale.format(':%S'),
    formatMinute = locale.format('%I:%M'),
    formatHour = locale.format('%I %p'),
    formatDay = locale.format('%a %d'),
    formatWeek = locale.format('%b %d'),
    formatMonth = locale.format('%B'),
    formatYear = locale.format('%Y'),
    formatInitial = locale.format(DATE_FORMAT_INITIAL);

export const multiFormat = date =>
    (d3.timeSecond(date) < date
        ? formatMillisecond
        : d3.timeMinute(date) < date
          ? formatSecond
          : d3.timeHour(date) < date
            ? formatMinute
            : d3.timeDay(date) < date
              ? formatHour
              : d3.timeMonth(date) < date
                ? d3.timeWeek(date) < date
                    ? formatDay
                    : formatWeek
                : d3.timeYear(date) < date
                  ? formatMonth
                  : formatYear)(date);

const roundUp = (n, f) => Math.ceil(n / f) * f;
const roundDown = (n, f) => Math.floor(n / f) * f;

export const getTenToPowerKoef = value => {
    const abs = Math.abs(value);
    const power = Math.log10(abs);
    const roundedPower = Math.floor(power);
    const tenToPower = round(10 ** roundedPower, -roundedPower);
    return tenToPower;
};

export const roundByReference = (key, extent = [], toZero = true) => {
    let [min, max] = extent;

    switch (key) {
        case 'WaterCut':
            {
                min = 0;
                max = 100;
            }
            break;
        default:
            {
                [min, max] = [min, max].map((value, ix) => {
                    if (isNil(value)) return null;
                    if (value === 0) return value;

                    const tenToPower = getTenToPowerKoef(value, key);
                    const valueDividedByTenToPower = Math.abs(value / tenToPower);
                    const sign = Math.sign(value);
                    const positive = sign > 0;
                    const negative = sign < 0;
                    let desiredRefValue;
                    if ((!ix && positive) || (ix && negative)) {
                        desiredRefValue = [...AXIS_REFERENCE_VALUES]
                            .reverse()
                            .find(refValue => valueDividedByTenToPower >= refValue);
                    }
                    if ((ix && positive) || (!ix && negative)) {
                        const [firstReferenceValue] = AXIS_REFERENCE_VALUES;
                        const extendedReferenceValues = [...AXIS_REFERENCE_VALUES, firstReferenceValue * 10];
                        desiredRefValue = extendedReferenceValues.find(
                            refValue => valueDividedByTenToPower <= refValue,
                        );
                    }
                    return sign * tenToPower * (desiredRefValue ?? 1);
                });

                if (toZero && max < 0) max = 0;
                if (toZero && min > 0) min = 0;
            }
            break;
    }
    return [min, max];
};

export const roundNearest = (key, extent) => {
    let [min, max] = extent;

    switch (key) {
        case 'WaterCut':
            {
                min = 0; // TODO: опционально
                max = 100;
            }
            break;
        default:
            {
                if (max > 0) {
                    min = 0; // TODO: опционально
                    switch (true) {
                        case max <= 1:
                            max = 1.0;
                            break;
                        case max <= 10:
                            max = roundUp(max, 10);
                            break;
                        case max < 100:
                            max = roundUp(max, 50);
                            break;
                        default:
                            max = roundUp(max, 500);
                            break;
                    }
                } else {
                    max = 0; // TODO: опционально
                    switch (true) {
                        case Math.abs(min) <= 1:
                            min = min > 0 ? 1.0 : -1.0;
                            break;
                        case Math.abs(min) <= 10:
                            min = roundUp(min, 10);
                            break;
                        case Math.abs(min) < 100:
                            min = roundDown(min, 50);
                            break;
                        default:
                            min = roundDown(min, 500);
                            break;
                    }
                }
            }
            break;
    }

    return [min, max];
};

/**
 * (x - x0)^2 + (y - y0)^2 = r^2 - формула окружности, где
 * x, y - координаты рассматриваемой точки,
 * x0, y0 - координаты центра окружности,
 * r - радиус окружности.
 * Принадлежность окружности определяется знаком <=
 */
export const belongsToCircle = (point = {}, circleParams = {}) => {
    const { x, y } = point;
    const { x0, y0, r } = circleParams;

    if ((x - x0) ** 2 + (y - y0) ** 2 <= r ** 2) return true;
    return false;
};

/**
 * x0 < x и x < x1 и y0 < y и y < y1 - формула принадлежности точки прямоугольнику
 * x, y - координаты рассматриваемой точки,
 * x0, y0 - координаты вехнего левого угла прямоугольника,
 * x1, y1 - координаты вехнего левого угла прямоугольника,
 */
export const belongsToRect = (point = {}, rectParams = {}) => {
    const { x, y } = point;
    const { x0, y0, width, height } = rectParams;

    const x1 = x0 + width;
    const y1 = y0 + height;

    const belongToX = x0 < x && x < x1;
    const belongToY = y0 < y && y < y1;

    if (belongToX && belongToY) return true;
    return false;
};

export function paint(selection, { attr = 'fill', data = selection.data() } = {}) {
    const color = data.color;
    const isFallback = color === '#fff';

    return selection.classed(`${attr}-black dark:${attr}-white`, !color || isFallback).attr(attr, color || null);
}

export function center(event, target, { x, y, width, height }) {
    if (event.sourceEvent) {
        const points = d3.pointers(event, target);
        return d3.mean(points, d => d[0]), d3.mean(points, d => d[1]);
    }

    return [x + width / 2, y + height / 2];
}
export const useMargin = margin => {
    Object.defineProperties(margin, {
        x: {
            get() {
                return this.left + this.right;
            },
        },
        y: {
            get() {
                return this.top + this.bottom;
            },
        },
    });

    return [margin];
};
